/**
 * Step labels (D13, docs/platform-agent/contracts.md §5): the short working line the panel shows for the step Amigo
 * is taking, in the person's language ("Checking the menu…"). A port of the agent's `agent/lib/step-labels.ts`
 * (A22) with the same table, so the panel and the agent's request log (§7) read alike; change both with §5.
 * Pure: no React, no i18n.
 */
import agentStepLabels from './agentStepLabels.json';

/** The languages the labels come in; any other falls back to English. */
export const STEP_LABEL_LANGUAGES = Object.freeze(['en', 'de']);

// QL77: the tables are the agent's own (docs/step-labels.json in foodamigos-platform-agent, written from
// agent/lib/step-labels.ts), copied here, so a tool the agent labels can't miss its label in the panel. Copy the file
// again with contracts §5 whenever the agent's table changes.
const freezeLabels = (labels) =>
  Object.freeze(Object.fromEntries(Object.entries(labels).map(([area, text]) => [area, Object.freeze({ ...text })])));

const fromAgent = (field) =>
  Object.freeze(
    Object.fromEntries(
      Object.entries(agentStepLabels.tools).map(([tool, row]) => [tool, row[field] ? freezeLabels(row[field]) : null]),
    ),
  );

/**
 * The labels of §5 by tool name, each tool's by area id; `*` is the label without an area (or for an area the table
 * doesn't name). null: the step isn't shown. Every label ends with "…".
 */
export const STEP_LABELS = fromAgent('labels');

/**
 * What a step that failed reads as (QA75, QL46, §5 Failed steps): what couldn't be done, in plain words and without
 * "…", for the area it worked in, so the panel never shows a vague step in red. null: a failed step isn't shown
 * (bookkeeping, and steps whose failure the answer itself explains).
 */
export const STEP_FAILED_LABELS = fromAgent('failed');

/** The label language for a language tag ("de", "de-DE", "en-GB"): its own when the table has it, else English. */
export function stepLabelLanguage(language) {
  const code = (language ?? '').trim().toLowerCase().slice(0, 2);

  return STEP_LABEL_LANGUAGES.includes(code) ? code : 'en';
}

function areaIn(input) {
  const area = input && typeof input === 'object' ? input.area : null;

  return typeof area === 'string' && area ? area : null;
}

/**
 * The area a step works in: the call's own `area` (area_describe, gap_record), else that of the turn's latest
 * area_describe before it. A read or a proposal carries no area, but Amigo describes an area before it reads or
 * proposes there.
 */
function areaOf(input, previous) {
  const own = areaIn(input);

  if (own) {
    return own;
  }

  for (let index = previous.length - 1; index >= 0; index -= 1) {
    const call = previous[index];

    if (call?.tool === 'foodamigos__area_describe') {
      const area = areaIn(call.input);

      if (area) {
        return area;
      }
    }
  }

  return null;
}

/**
 * What a step reads as in the panel, in the person's language (§5): the label of `toolName` for the area it works in,
 * or the tool's label without an area. `previous` are the turn's earlier calls (`[{ tool, input }]`), in order. null
 * when the step isn't shown (show_card, request_log) or the table doesn't name the tool (loading a skill,
 * ask_question).
 */
export function stepLabel(toolName, input, language, previous = []) {
  const labels = Object.hasOwn(STEP_LABELS, toolName) ? STEP_LABELS[toolName] : null;

  if (!labels) {
    return null;
  }

  const area = areaOf(input, previous ?? []);
  const text = (area !== null && Object.hasOwn(labels, area) ? labels[area] : null) ?? labels['*'];

  return text[stepLabelLanguage(language)];
}

/**
 * Which of a failed step's labels applies (QL46): the area it worked in (found like `stepLabel`'s) when the failed
 * labels of `toolName` name that area, else `*`. null when a failed step isn't shown; undefined for a tool the table
 * doesn't know (a tool added to the agent before the panel), which keeps the step's own label. The panel's step list
 * reads the label by this area from its catalog (`failed_steps`), so it comes in every language the dashboard has.
 */
export function stepFailedArea(toolName, input, previous = []) {
  if (!Object.hasOwn(STEP_FAILED_LABELS, toolName)) {
    return undefined;
  }

  const labels = STEP_FAILED_LABELS[toolName];

  if (!labels) {
    return null;
  }

  const area = areaOf(input, previous ?? []);

  return area !== null && Object.hasOwn(labels, area) ? area : '*';
}

/**
 * What a failed step reads as, in the person's language (§5 Failed steps, as the agent's request log reads it): the
 * failed label of `toolName` for the area it worked in, or the tool's without an area. null when a failed step isn't
 * shown; undefined for a tool the table doesn't know.
 */
export function stepFailedLabel(toolName, input, language, previous = []) {
  const area = stepFailedArea(toolName, input, previous);

  if (area === null || area === undefined) {
    return area;
  }

  return STEP_FAILED_LABELS[toolName][area][stepLabelLanguage(language)];
}
